import { useMemo, useCallback, useRef, useState } from "react"
import MetricSelector from "components/custom-metrics-selector"
import { useMetricsSelectorAnalytics } from "components/custom-metrics-selector/hooks/use-metrics-selector-analytics"
import { ModelMetadata } from "resources/models"
import type { TemplateResponse } from "resources/templates"
import { DEFAULT_GRAPH_STATE } from "connected-components/dashboard/store/graph-slice"
import { MODULE_TYPES } from "connected-components/dashboard/modules/types"
import { OK, ERROR, LOADING } from "helpers/data-statuses"
import { GRAPH_CUSTOM_TEMPLATE_TYPE } from "../template-chart-config"
import { useGraphData } from "../../hooks/use-graph-data"
import { buildCustomGraphAvailableMetrics, buildContentSetCustomGraphAvailableMetrics } from "./helpers"
import { encodeFamilyMetricIds, CUSTOM_GRAPH_FAMILY_LABELS, type CustomGraphFamilyContext } from "./family"
import { useCustomGraphContentSetMetadata } from "./use-custom-graph-content-set-metadata"

// Stable module-level fallback — never a fresh `?? []` literal, which would re-render every consumer.
const EMPTY_ARRAY: string[] = []

interface CustomGraphMetricsSelectorProps {
  modelMetadata: ModelMetadata
  familyContext: CustomGraphFamilyContext
  templateMetadata?: TemplateResponse
  initialViewName?: string | null
  initialSelectedMetrics?: string[] | null
  duplicateSourceTemplateId?: string | null
}

export default function CustomGraphMetricsSelector({
  modelMetadata,
  familyContext,
  templateMetadata,
  initialViewName,
  initialSelectedMetrics,
  duplicateSourceTemplateId,
}: CustomGraphMetricsSelectorProps) {
  const graphContext = useGraphData()
  const {
    showMetricsSelector,
    setShowMetricsSelector,
    setSelectedTemplate,
    selectedTemplate,
    modelId,
    saveTemplate,
    deleteTemplate,
    clearDuplicateState,
    getCurrentViewId,
  } = graphContext
  const analytics = useMetricsSelectorAnalytics(graphContext, MODULE_TYPES.GRAPH)

  const { contentSetMetadata, contentSetsStatus } = useCustomGraphContentSetMetadata(familyContext)

  const availableMetrics = useMemo(() => (
    familyContext.family === "content-set"
      ? buildContentSetCustomGraphAvailableMetrics(contentSetMetadata)
      : buildCustomGraphAvailableMetrics(modelMetadata)
  ), [familyContext, modelMetadata, contentSetMetadata])

  const title = useMemo(() => {
    const baseTitle = `Select ${CUSTOM_GRAPH_FAMILY_LABELS[familyContext.family]} Metrics`
    if (familyContext.family !== "content-set") return baseTitle
    if (contentSetsStatus === LOADING) return `${baseTitle} (Loading content sets...)`
    if (contentSetsStatus === ERROR) return `${baseTitle} (Failed to load content sets)`
    return baseTitle
  }, [familyContext, contentSetsStatus])

  const handleCloseWithoutRestore = useCallback(() => {
    if (showMetricsSelector) {
      analytics.trackClosed(templateMetadata)
    }
    setShowMetricsSelector(false, { restoreSourceView: false })
  }, [analytics, showMetricsSelector, setShowMetricsSelector, templateMetadata])

  const handleToggle = useCallback(() => {
    if (showMetricsSelector) {
      analytics.trackClosed(templateMetadata)
    } else {
      analytics.trackOpened(templateMetadata)
    }
    setShowMetricsSelector(!showMetricsSelector, { restoreSourceView: showMetricsSelector })
  }, [analytics, showMetricsSelector, setShowMetricsSelector, templateMetadata])

  const isSavingTemplateRef = useRef(false)
  const [isSaving, setIsSaving] = useState(false)

  const handleSaveCustomTemplate = useCallback(async (template: { name: string, metrics: string[] }) => {
    if (familyContext.family === "content-set" && contentSetsStatus !== OK) {
      return
    }
    if (isSavingTemplateRef.current) return
    isSavingTemplateRef.current = true
    setIsSaving(true)

    const metricsCount = template.metrics?.length ?? 0

    let savedTemplate
    try {
      savedTemplate = await saveTemplate({
        id: templateMetadata?.id,
        type: GRAPH_CUSTOM_TEMPLATE_TYPE,
        name: template.name,
        metrics: encodeFamilyMetricIds(familyContext, template.metrics),
      })
    } catch {
      return
    } finally {
      isSavingTemplateRef.current = false
      setIsSaving(false)
    }
    if (savedTemplate) {
      if (templateMetadata?.id) {
        analytics.trackViewUpdated(templateMetadata.id, template.name, metricsCount)
      } else {
        analytics.trackViewSaved(template.name, metricsCount)
      }
      clearDuplicateState()
      if (selectedTemplate !== savedTemplate.id) {
        setSelectedTemplate(savedTemplate.id)
      }
    }
  }, [analytics, saveTemplate, setSelectedTemplate, clearDuplicateState, templateMetadata, selectedTemplate, familyContext, contentSetsStatus])

  const isDeletingTemplateRef = useRef(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDeleteCustomTemplate = useCallback(async (template: { id: string, name: string }) => {
    if (isDeletingTemplateRef.current) return
    isDeletingTemplateRef.current = true
    setIsDeleting(true)
    try {
      await deleteTemplate(template)
    } catch {
      return
    } finally {
      isDeletingTemplateRef.current = false
      setIsDeleting(false)
    }
    analytics.trackViewDeleted(template.id, template.name)
    if (template.id === getCurrentViewId()) {
      setSelectedTemplate(DEFAULT_GRAPH_STATE.selectedTemplate)
    }
  }, [analytics, deleteTemplate, setSelectedTemplate, getCurrentViewId])

  const duplicateDataKey = initialSelectedMetrics ? (duplicateSourceTemplateId ?? "built-in") : ""

  return <MetricSelector
    key={showMetricsSelector ? `${templateMetadata?.id || "new"}-${showMetricsSelector}-${modelId}-${duplicateDataKey}` : "closed"}
    modelId={modelId}
    isOpen={showMetricsSelector}
    toggle={handleToggle}
    closeWithoutRestore={handleCloseWithoutRestore}
    title={title}
    showColumnOrdering={false}
    defaultSelectedMetrics={initialSelectedMetrics ?? EMPTY_ARRAY}
    availableMetrics={availableMetrics}
    saveCustomTemplate={handleSaveCustomTemplate}
    deleteCustomTemplate={handleDeleteCustomTemplate}
    customTemplate={templateMetadata}
    analytics={analytics}
    initialViewName={initialViewName}
    isSaving={isSaving}
    isDeleting={isDeleting}
  />
}
